import { SystemIcon } from '@ausbom/icon';
import Tick16 from '@ausbom/icon/lib/icons/system/Tick16';
import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { Copy16 } from '../Icons/Icons';
import './CopyButton.scss';

export type CopyButtonProps = {
  copyText?: string;
  label?: string;
  copiedLabel?: string;
  className?: string;
  disabled?: boolean;
  onCopied?: () => void;
  hoverToast?: string;
  gapLeft?: number | string;
  type?: 'button' | 'submit' | 'reset';
  id?: string;
  name?: string;
  value?: string | number | readonly string[];
  autoFocus?: boolean;
  tabIndex?: number;
  'aria-label'?: string;
  'aria-describedby'?: string;
};

const HIDE_DELAY_MS = 500;

const CopyButton = forwardRef<HTMLButtonElement, CopyButtonProps>(
  (
    {
      copyText = '',
      label = 'Copy',
      copiedLabel = 'Copied!',
      className,
      disabled = false,
      onCopied,
      hoverToast,
      gapLeft = 8,
      type = 'button',
      id,
      name,
      value,
      autoFocus,
      tabIndex,
      'aria-label': ariaLabel,
      'aria-describedby': ariaDescribedBy
    },
    ref
  ) => {
    const [pressed, setPressed] = useState(false);
    const [hoveringButton, setHoveringButton] = useState(false);

    // NEW: sticky toast text that doesn't change during fade-out
    const [toastText, setToastText] = useState<string>(hoverToast ?? 'Copy');

    // Hide timer
    const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const clearHideTimer = () => {
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
    };

    const startHideTimer = () => {
      if (hideTimerRef.current) return;
      hideTimerRef.current = setTimeout(() => {
        hideTimerRef.current = null;
        // Hide the toast and reset "pressed" — but DO NOT change toastText here
        setHoveringButton(false);
        setPressed(false);
      }, HIDE_DELAY_MS);
    };

    useEffect(() => {
      return () => clearHideTimer();
    }, []);

    const handleClick = useCallback(async () => {
      if (disabled) return;
      await navigator.clipboard.writeText(copyText);
      setPressed(true);
      setToastText(copiedLabel); // show "Copied!" and keep it while visible
      onCopied?.();
    }, [copyText, disabled, onCopied, copiedLabel]);

    const showToast = hoveringButton && !disabled;

    return (
      <div
        className={['copy-btn', className ?? ''].filter(Boolean).join(' ')}
        style={{ marginLeft: typeof gapLeft === 'number' ? `${gapLeft}px` : gapLeft }}
        onMouseEnter={() => {
          clearHideTimer();
          setHoveringButton(true);
          // When showing due to hover and not in "pressed" (success) state,
          // set the default hover text.
          if (!pressed) setToastText(hoverToast ?? 'Copy');
        }}
        onMouseLeave={() => {
          startHideTimer();
        }}
        onFocus={() => {
          clearHideTimer();
          setHoveringButton(true);
          if (!pressed) setToastText(hoverToast ?? 'Copy');
        }}
        onBlur={() => {
          startHideTimer();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape' || e.key === 'Esc') {
            clearHideTimer();
            setHoveringButton(false);
            setPressed(false);
            // Note: we do NOT change toastText here either.
          }
        }}
      >
        <button
          ref={ref}
          type={type}
          id={id}
          name={name}
          value={value as any}
          autoFocus={autoFocus}
          tabIndex={tabIndex}
          className="copy-btn__button"
          disabled={disabled}
          aria-label={ariaLabel || label}
          aria-describedby={ariaDescribedBy}
          onClick={handleClick}
          onFocus={() => {
            clearHideTimer();
            setHoveringButton(true);
            if (!pressed) setToastText(hoverToast ?? 'Copy');
          }}
        >
          <SystemIcon icon={Copy16} size={16} aria-hidden />
        </button>

        <div
          className={`copy-btn__toast ${showToast ? 'is-visible' : ''} ${pressed ? 'is-success' : ''}`}
          role="presentation"
        >
          {pressed && <SystemIcon icon={Tick16} size={16} aria-hidden primaryColor="#ffffff" />}
          {toastText}
        </div>

        {/* SR-only region for accessibility */}
        <span role="status" aria-live="polite" className="copy-btn__sr-only">
          {pressed ? copiedLabel : ''}
        </span>
      </div>
    );
  }
);

CopyButton.displayName = 'CopyButton';

export default CopyButton;
